import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PopupPromptDialogComponent } from '@rd/popup/components/prompt-dialog/prompt-dialog.component';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { CheckoutErrorDialogComponent } from '@rd/shared-legacy/components/checkout-error-dialog/checkout-error-dialog.component';
import { ProductCategory } from '@rd/types';
import { HttpError } from '@rd/types/http-error.type';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import * as actions from '../actions/cart-item.actions';
import { CartItemApiService } from '../services/cart-item-api/cart-item-api.service';
import { AddCartItemParam } from '../types/cart-item.type';
import { Cart, CartPromocodePostParams } from '../types/cart.type';

@Injectable()
export class CartItemEffects {
  updateCartItem = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CartItemActionTypes.UPDATE_CART_ITEM_QUANTITY),
      concatMap(({ id, quantity }) => {
        return this.cartItemApiService
          .updateCartItemQuantity({ id, quantity })
          .pipe(
            map(() => actions.UpdateCartItemQuantitySuccess()),
            catchError((error: HttpError) => {
              return of(actions.UpdateCartItemQuantityFail(error));
            }),
          );
      }),
    ),
  );
  updateCartItemQuantitySuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CartItemActionTypes.UPDATE_CART_ITEM_QUANTITY_SUCCESS),
      map(() => actions.LoadCart()),
    ),
  );
  updateCartItemQuantityFail = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CartItemActionTypes.UPDATE_CART_ITEM_QUANTITY_FAIL),
      map(() => actions.LoadCart()),
    ),
  );
  removeCartItem = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CartItemActionTypes.REMOVE_CART_ITEM),
      switchMap(({ id }) => {
        return this.cartItemApiService.removeCartItem(id).pipe(
          map(() => actions.RemoveCartItemsSuccess()),
          catchError((error: HttpError) => {
            return of(actions.RemoveCartItemsFail(error));
          }),
        );
      }),
    ),
  );
  // Reload cart items if a delete is successful. Separate this effect from the removeCartItem
  removeCartItemSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CartItemActionTypes.REMOVE_CART_ITEM_SUCCESS),
      map(() => actions.LoadCart()),
    ),
  );

  addCartItem = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CartItemActionTypes.ADD_CART_ITEM),
      switchMap(
        ({
          productName,
          productId,
          brand,
          quantity,
          cashAmount,
          pointsAmount,
          pointsPercentage,
        }: AddCartItemParam & { type: string }) =>
          this.cartItemApiService
            .postCartItem({
              cartItem: {
                quantity,
                cashAmount,
                pointsAmount,
                supplierId: productId,
                productType: ProductCategory.Merchandise,
                orderDetails: {
                  product_name: productName,
                  points_percentage: pointsPercentage,
                  brand,
                  recipient: {},
                },
              },
            })
            .pipe(
              map(() => actions.AddCartItemSuccess({ productName, quantity })),
              catchError((error: HttpError) =>
                of(actions.AddCartItemFail({ error, productName, quantity })),
              ),
            ),
      ),
    ),
  );

  addCartItemSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CartItemActionTypes.ADD_CART_ITEM_SUCCESS),
      map(({ productName, quantity }) => {
        // TODO: Use actual translation here - https://github.com/Kaligo/e2e-rewards-dashboard/pull/2946/files
        // TODO: Pluralization
        const tempDialogTitle = `${
          quantity > 1 ? 'Items' : 'Item'
        } added to your cart!`;

        return actions.LoadCartAfterAddingItem({
          addToCartPromptDialogData: {
            dialogTitle: tempDialogTitle,
            productName,
            quantity,
          },
        });
      }),
    ),
  );

  addCartItemFail = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CartItemActionTypes.ADD_CART_ITEM_FAIL),
      withLatestFrom(
        this.actions$.pipe(ofType(actions.CartItemActionTypes.ADD_CART_ITEM)),
      ),
      tap(([fail, add]) => {
        const { productName, error } = fail;
        const { maxPurchaseQuantity } = add;
        this.dialogService.open(CheckoutErrorDialogComponent, {
          data: {
            interpolateData: {
              productType: 'merchandise',
              maxPurchaseQuantity,
              productName,
            },
            error,
            iconName: 'unexpected-error',
          },
          id: 'gc-cart-item-error-dialog',
          screenMode: 'center-md',
        });
      }),
      map(() => actions.LoadCart()),
    ),
  );

  // New cart api related effects

  loadCart = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.CartItemActionTypes.LOAD_CART,
        actions.LoadCartAfterAddingItem,
      ),
      switchMap((action) => {
        return this.cartItemApiService.getCart().pipe(
          map((cart: Cart) => {
            if (action.type === actions.LoadCartAfterAddingItem.type) {
              const { dialogTitle, productName, quantity } =
                action.addToCartPromptDialogData;

              this.dialogService.open(PopupPromptDialogComponent, {
                data: {
                  title: dialogTitle,
                  body: 'shopping_cart.label.added_to_cart_content',
                  bodyData: {
                    productName: productName,
                    quantity,
                  },
                  primaryButtonClick: () => {
                    this.router.navigateByUrl('/shopping-cart/home');
                  },
                  primaryButtonLabel: 'shopping_cart.label.go_to_cart',
                  secondaryButtonLabel: 'shopping_cart.label.continue_shopping',
                  showCloseButton: true,
                },
                ariaLabel: dialogTitle,
                id: 'gc-add-to-cart-dialog',
                screenMode: 'center-md',
              });
            }

            return actions.LoadCartSuccess({ cart });
          }),
          catchError((error: HttpError) => {
            return of(actions.LoadCartFail(error));
          }),
        );
      }),
    ),
  );

  applyCartPromocode = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CartItemActionTypes.ADD_CART_PROMOCODE),
      switchMap(
        (cartPromocodePostParams: CartPromocodePostParams & { type: string }) =>
          this.cartItemApiService.applyPromocode(cartPromocodePostParams).pipe(
            map((cart) =>
              actions.AddCartPromocodeSuccess({
                cart,
              }),
            ),
            catchError((error: HttpError) =>
              of(actions.AddCartPromocodeFail(error)),
            ),
          ),
      ),
    ),
  );

  removeCartPromocode = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CartItemActionTypes.REMOVE_CART_PROMOCODE),
      switchMap(() =>
        this.cartItemApiService.removePromocode().pipe(
          map(() => actions.RemoveCartPromocodeSuccess()),
          catchError((error: HttpError) => {
            return of(actions.RemoveCartPromocodeFail(error));
          }),
        ),
      ),
    ),
  );

  removeCartExpiredPromocode = createEffect(
    () =>
      // This is a side effect to handle implicit removal of promocode
      // to show the promocode expiry once. In this case we do not refresh
      // the cart, hence we have a separate action and a effect
      this.actions$.pipe(
        ofType(actions.CartItemActionTypes.REMOVE_CART_EXPIRED_PROMOCODE),
        switchMap(() => this.cartItemApiService.removePromocode()),
      ),
    { dispatch: false },
  );

  removeCartPromocodeSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CartItemActionTypes.REMOVE_CART_PROMOCODE_SUCCESS),
      map(() => actions.LoadCart()),
    ),
  );

  constructor(
    private actions$: Actions,
    private cartItemApiService: CartItemApiService,
    private dialogService: DialogService,
    private router: Router,
  ) {}
}
